import type {
  ApplicantListFilterObject,
  ApplicantListFilter,
  ApplicantListFilterMetadata,
} from 'src/types/crm';
import moment from 'moment';

// export const INFO_TOOLTIP = {};

// will be removed after api integration - @swatantra
export function getRandomDateWithin30Days(): Date {
  const today = new Date();
  const maxDays = 30;

  // Generate a random number of days between 0 and 30
  const randomDays = Math.floor(Math.random() * maxDays);

  // Add the random number of days to today's date
  const randomDate = new Date();
  randomDate.setDate(today.getDate() + randomDays);

  return randomDate;
}

export function getDifferenceInDays(date: Date, language?: string): number {
  const today = moment(); // Get today's date
  const givenDate = moment(date).locale(language || 'en'); // Parse the given date

  // Calculate the difference in days
  const differenceInDays = givenDate.diff(
    today.locale(language || 'en'),
    'days',
  );

  return -1 * differenceInDays;
}

export const debounceTime = 400;

export const splitCommaSeparatedArray = (arr: Array<string>): Array<string> => {
  return arr.flatMap((item) =>
    typeof item === 'string' && item.includes(',')
      ? item
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      : [item],
  );
};

export function convertTagsPayload(arr: any[]) {
  if (!Array.isArray(arr)) {
    return [];
  }
  if (arr.length === 0) {
    return arr;
  }

  let normalized = arr.map((a) => (Array.isArray(a) ? a : [a]));

  const maxLen = Math.max(...normalized.map((a) => a.length));

  normalized = normalized.map((group) =>
    group.length === 1 ? Array(maxLen).fill(group[0]) : group,
  );

  const result = Array.from({length: maxLen}, (_, i) =>
    normalized.map((group) => group[i]).filter((v) => v !== undefined),
  );

  return result;
}

export const getAppliedFilterKeys = (
  filterState: ApplicantListFilterObject,
) => {
  if (!filterState || typeof filterState !== 'object') {
    return [];
  }

  const {
    tags,
    skills,
    skillsBool,
    booleanSkills,
    excludeSkills,
    gender,
    source,
    subsource,
    medium,
    dateRange,
    experienceStart,
    experienceEnd,
    colleges,
    degrees,
    majors,
    pgGradeStart,
    pgGradeEnd,
    ugGradeStart,
    ugGradeEnd,
    expectedCtcStart,
    expectedCtcEnd,
    expectedCtcCurrency,
    currentCtcStart,
    currentCtcEnd,
    currentCtcCurrency,
    noticePeriodStart,
    noticePeriodEnd,
    noticePeriod,
    preferredLocations,
    locations,
    companies,
    currentCompanies,
    excludeCompanies,
    keywords,
    excludeKeywords,
    booleanKeywords,
    keywordsBool,
    chatInvite,
    address,
    includedTagIds,
    excludedTagIds,
    includedTalentPoolIds,
    excludedTalentPoolIds,
    jobCandidateCustomFilters,
    candidateCustomFilters,
    talentPoolStatusFilters,
  } = filterState;
  const selectedKeys: Array<string> = [];

  if ((chatInvite || []).length > 0) {
    selectedKeys.push('chatbotScreening');
  }

  if (
    (tags || []).length > 0 ||
    (includedTagIds || []).length > 0 ||
    (excludedTagIds || []).length > 0
  ) {
    selectedKeys.push('tags');
  }
  if (
    (includedTalentPoolIds || []).length > 0 ||
    (excludedTalentPoolIds || []).length > 0
  ) {
    selectedKeys.push('talentPools');
  }
  if (
    (skills || []).length > 0 ||
    skillsBool ||
    Boolean(booleanSkills) ||
    (excludeSkills || []).length > 0
  ) {
    selectedKeys.push('skills');
  }
  if (Array.isArray(gender) && gender.length > 0) {
    selectedKeys.push('gender');
  }
  if (Boolean(source) || Boolean(subsource) || Boolean(medium)) {
    selectedKeys.push('source');
  }
  if (dateRange?.startDate || dateRange?.endDate) {
    selectedKeys.push('addedOn');
  }
  if (experienceStart !== -1 || experienceEnd !== -1) {
    selectedKeys.push('experienceRange');
  }
  if (
    (colleges || []).length > 0 ||
    (degrees || []).length > 0 ||
    (majors || []).length > 0 ||
    pgGradeStart > 0 ||
    pgGradeEnd > 0 ||
    ugGradeStart > 0 ||
    ugGradeEnd > 0
  ) {
    selectedKeys.push('education');
  }
  if (
    Boolean(expectedCtcStart) ||
    Boolean(expectedCtcEnd) ||
    Boolean(expectedCtcCurrency)
  ) {
    selectedKeys.push('expectedSalaryRange');
  }
  if (
    Boolean(currentCtcStart) ||
    Boolean(currentCtcEnd) ||
    Boolean(currentCtcCurrency)
  ) {
    selectedKeys.push('currentSalaryRange');
  }
  if (
    Boolean(noticePeriodStart) ||
    Boolean(noticePeriodEnd) ||
    Boolean(noticePeriod)
  ) {
    selectedKeys.push('noticePeriodRange');
  }
  if (
    (preferredLocations || []).length > 0 ||
    (locations || []).length > 0 ||
    (address || []).length > 0
  ) {
    selectedKeys.push('location');
  }
  if (
    (companies || []).length > 0 ||
    (currentCompanies || []).length > 0 ||
    (excludeCompanies || []).length > 0
  ) {
    selectedKeys.push('companies');
  }
  if (
    (keywords || []).length > 0 ||
    keywordsBool ||
    Boolean(booleanKeywords) ||
    (excludeKeywords || []).length > 0
  ) {
    selectedKeys.push('keywords');
  }
  if (
    (jobCandidateCustomFilters || []).length > 0 ||
    (candidateCustomFilters || []).length > 0
  ) {
    selectedKeys.push('customFilter');
  }
  if ((talentPoolStatusFilters || []).length > 0) {
    selectedKeys.push('talentPoolStatusFilters');
  }
  return selectedKeys;
};

export function convertForPayloads(fields: any[] = []) {
  if (!Array.isArray(fields)) {
    return [];
  }
  return fields.map((field) => {
    if (
      field.customFieldType === 'SINGLE_SELECT' ||
      field.customFieldType === 'TEXT'
    ) {
      let values = field.values;
      if (!Array.isArray(values)) {
        values = [values];
      }
      values = splitCommaSeparatedArray(values);
      return {
        ...field,
        values,
      };
    }
    if (field.customFieldType === 'DATE') {
      return {
        ...field,
        values: field.values.map((dateStr: string) =>
          new Date(dateStr).getTime(),
        ),
      };
    }
    if (field.customFieldType === 'DATE_RANGE') {
      return {
        ...field,
        customFieldType: 'DATE',
        values: field.values.map((dateStr: string) => Number(dateStr)),
      };
    } else if (field.customFieldType === 'NUMBER_RANGE') {
      if (field.values[0] === 'NaN') {
        field.values[0] = null;
      }
      if (field.values[1] === 'NaN') {
        field.values[1] = null;
      } else if (field.values[1] === undefined) {
        field.values[1] = null;
      }
      return {
        ...field,
        customFieldType: 'NUMBER',
      };
    }
    return field;
  });
}

export function transformApplicantListFilter(
  filterState: ApplicantListFilter,
  options: {
    applicantViewType: 'list' | 'kanban';
    applicantListFilterMetadata?: ApplicantListFilterMetadata | null;
    page?: number;
  },
): ApplicantListFilter {
  const {applicantViewType, applicantListFilterMetadata, page} = options;

  // Transform address array (split strings by '~')
  const myAddress = filterState.filter.address.map((address) => {
    if (typeof address === 'string') {
      const keys = address.split('~');
      return {
        city: keys[0] || '',
        county: keys[1] || '',
        postalCode: keys[2] || '',
        label: keys[3] || '',
        state: keys[4] || '',
        country: keys[5] || '',
      };
    } else {
      return address;
    }
  });

  const includedTagIds = convertTagsPayload(
    filterState.filter.includedTagIds || [],
  );

  // @ts-expect-error - TS2322 - Type 'string[]' is not assignable to type '{ city: string; county: string; postalCode: string; label: string; state: string; country: string; }[]'.
  filterState.filter.address = myAddress;

  const tempFilter = {
    ...filterState,
    ...(page !== undefined && {page}),
    filter: {
      ...filterState.filter,
      address: myAddress,
      includedTagIds,
      stageIds:
        applicantViewType === 'kanban'
          ? applicantListFilterMetadata?.stages?.map((stage: any) => stage.id)
          : filterState.filter.stageIds,
    },
    candidateCustomFilters:
      convertForPayloads(filterState.filter?.candidateCustomFilters) ||
      undefined,
    jobCandidateCustomFilters:
      convertForPayloads(filterState.filter?.jobCandidateCustomFilters) ||
      undefined,
  } as unknown as ApplicantListFilter;

  return tempFilter;
}
